import { Fragment, type ReactNode } from 'react'
import { cn } from '../../lib/utils'
import { KuficSkeleton } from '../kufic/kufic-skeleton'
import type { BadgeTone } from './badge'

export interface SummaryStripItem {
  label: ReactNode
  value: ReactNode
  count?: ReactNode
  tone?: BadgeTone
  onClick?: () => void
  selected?: boolean
  /** Hide a detail figure below 640px container width; keep it on wider strips. */
  phone?: 'hidden'
  testId?: string
}

export interface SummaryStripProps {
  items: SummaryStripItem[]
  loading?: boolean
  className?: string
  role?: 'group'
  'aria-label'?: string
  'data-testid'?: string
}

const TONE_CLASS: Record<BadgeTone, string> = {
  neutral: 'before:bg-muted-foreground',
  success: 'before:bg-success',
  warning: 'before:bg-warning',
  info: 'before:bg-info',
  danger: 'before:bg-danger',
  signal: 'before:bg-brand',
}

function SummaryStrip({ items, loading = false, className, ...props }: SummaryStripProps) {
  // A phone separator sits only between two parts a phone shows: never after a hidden part or the last visible one
  const lastOnPhone = items.findLastIndex((item) => item.phone !== 'hidden')
  return (
    <div
      data-slot="summary-strip"
      className={cn(
        '@container flex w-full min-w-0 flex-wrap gap-x-2 gap-y-1 border-y border-border py-2 text-sm',
        className,
      )}
      {...props}
    >
      {items.map((item, index) => (
        <Fragment key={item.testId ?? String(item.label)}>
          <SummaryPart item={item} index={index} loading={loading} />
          {index < items.length - 1 ? (
            <span
              aria-hidden="true"
              data-summary-separator
              className={cn(
                'text-muted-foreground @min-[40rem]:hidden',
                (item.phone === 'hidden' || index >= lastOnPhone) && '@max-[40rem]:hidden',
              )}
            >
              {' · '}
            </span>
          ) : null}
        </Fragment>
      ))}
    </div>
  )
}

function SummaryPart({
  item,
  index,
  loading,
}: {
  item: SummaryStripItem
  index: number
  loading: boolean
}) {
  const className = cn(
    'inline-flex min-w-0 shrink-0 items-center gap-1 whitespace-nowrap',
    "before:size-1.5 before:shrink-0 before:content-['']",
    item.tone ? TONE_CLASS[item.tone] : 'before:hidden',
    item.phone === 'hidden' && '@max-[40rem]:hidden',
    '@min-[40rem]:min-w-0 @min-[40rem]:flex-1 @min-[40rem]:flex-col @min-[40rem]:items-start @min-[40rem]:gap-1 @min-[40rem]:whitespace-normal @min-[40rem]:px-4 @min-[40rem]:first:ps-0',
    index > 0 && '@min-[40rem]:border-s @min-[40rem]:border-border-subtle',
    item.onClick && 'cursor-pointer text-start hover:bg-accent',
  )
  const content = (
    <>
      <span className="text-muted-foreground">{item.label}</span>
      {loading ? (
        <KuficSkeleton className="w-24" />
      ) : (
        <span className="font-medium tabular-nums whitespace-nowrap">{item.value}</span>
      )}
      {!loading && item.count !== undefined ? (
        <span className="text-muted-foreground">
          <span aria-hidden="true"> · </span>
          {item.count}
        </span>
      ) : null}
    </>
  )

  return item.onClick ? (
    <button
      type="button"
      data-summary-part
      data-testid={item.testId}
      data-tone={item.tone}
      aria-pressed={item.selected}
      onClick={item.onClick}
      className={className}
    >
      {content}
    </button>
  ) : (
    <div data-summary-part data-testid={item.testId} data-tone={item.tone} className={className}>
      {content}
    </div>
  )
}

export { SummaryStrip }
